import React, { useState } from 'react';
import { useAppContext } from 'src/components/app-context';
import { H4Text } from '@prisjakt/ui/text/variants';
import { OutlinedButton } from '@prisjakt/ui/button/variants';
import { SizeVariantSelectionButton } from './size-variant-selection-button';
import { useOfferListFilterContext } from 'src/areas/product/components/offer-list-filter-context';
import type { IVariantsProps, IVariantValue } from '../types';
import { FilterType } from 'src/components/price-list/types';
import { getGroupedVariants } from './utils/group-size-variants';
import { VariantGridTitle, ScrollableArea } from '../styles-common';
import {
  StyledShowMoreButtonWrapper,
  VariantGridCell,
  VariantGridWrapper,
} from './styles';
import { getSelectedAndFirstFewVariants } from './utils/collapsing';

export const SizeVariantsGrid: React.FC<IVariantsProps> = props => {
  const { title, variants, handleSelection, isMobile } = props;
  const { t } = useAppContext();
  const { getFilter } = useOfferListFilterContext();
  const [isExpanded, setIsExpanded] = useState(false);

  const filter = getFilter(FilterType.VARIANT_SIZE);
  const selection = filter?.active ? filter?.selection : undefined;

  const groupedVariants = getGroupedVariants(variants?.filter(Boolean));

  const LIMIT_CELLS = isMobile ? 12 : Number.MAX_VALUE;

  const selectAllCell = (
    <VariantGridCell>
      <SizeVariantSelectionButton
        handleSelection={handleSelection}
        title={t('product', 'variants.all')}
        selection={selection}
        variant={null}
        isMobile={isMobile}
      />
    </VariantGridCell>
  );

  const variantGroupCells = (
    i: number,
    variantGroup: IVariantValue[],
    showSelectAll: boolean
  ) => {
    const key = `size-group-${i}`;
    const variantsToDisplay = isExpanded
      ? variantGroup
      : getSelectedAndFirstFewVariants(
          variantGroup,
          showSelectAll,
          LIMIT_CELLS,
          selection
        );

    return (
      <VariantGridWrapper key={key}>
        {showSelectAll && selectAllCell}
        {variantsToDisplay.map(sizeVariant => (
          <VariantGridCell key={sizeVariant.size}>
            <SizeVariantSelectionButton
              handleSelection={handleSelection}
              title={sizeVariant.size!}
              variant={sizeVariant.size!}
              selection={selection}
              disabled={sizeVariant.disabled}
              isMobile={isMobile}
            />
          </VariantGridCell>
        ))}

        {isMobile && variantGroup.length > LIMIT_CELLS && (
          <StyledShowMoreButtonWrapper>
            <OutlinedButton
              fullWidth
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded
                ? t('common', 'button.show-less')
                : t('common', 'button.show-more')}
            </OutlinedButton>
          </StyledShowMoreButtonWrapper>
        )}
      </VariantGridWrapper>
    );
  };

  return (
    <div>
      {title && (
        <VariantGridTitle>
          <H4Text>{title}</H4Text>
        </VariantGridTitle>
      )}
      <ScrollableArea>
        {groupedVariants.map((group, i) =>
          variantGroupCells(i, group, i === 0)
        )}
      </ScrollableArea>
    </div>
  );
};
